import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Repeat, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

import { planningAPI } from "../services/planningService";
import CyclePatternCard from "./schedule/CyclePatternCard";
import CycleCreateDialog from "./schedule/CycleCreateDialog";
import CycleEditDialog from "./schedule/CycleEditDialog";
import CycleDeleteDialog from "./schedule/CycleDeleteDialog";

const emptyForm = { name: "", description: "", cycle_duration_weeks: 2, cycle_weeks: [] };

const CyclePatternsManager = () => {
  const { t } = useTranslation();
  const [cyclePatterns, setCyclePatterns] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [expandedPattern, setExpandedPattern] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadCyclePatterns(), loadSchedules()]);
    } catch (error) {
      toast.error(t("planning.legacy.cycles.errors.loadData"));
    } finally {
      setLoading(false);
    }
  };

  const loadCyclePatterns = async () => {
    try {
      const data = await planningAPI.cyclePatterns.getCyclePatterns({ limit: 100 });
      setCyclePatterns(data.items || []);
    } catch (error) {
    }
  };

  const loadSchedules = async () => {
    try {
      const data = await planningAPI.schedules.getSchedules({ is_active: true, limit: 100 });
      setSchedules(data.items || []);
    } catch (error) {
    }
  };

  const resetForm = () => setFormData(emptyForm);

  const handleOpenCreateModal = () => { resetForm(); setShowCreateModal(true); };

  const handleOpenEditModal = (pattern) => {
    setSelectedPattern(pattern);
    setFormData({
      name: pattern.name,
      description: pattern.description || "",
      cycle_duration_weeks: pattern.cycle_duration_weeks,
      cycle_weeks: pattern.cycle_weeks || [],
    });
    setShowEditModal(true);
  };

  const handleOpenDeleteDialog = (pattern) => {
    setSelectedPattern(pattern);
    setShowDeleteDialog(true);
  };

  const handleCreatePattern = async () => {
    try {
      if (!formData.name.trim()) { toast.error(t("planning.legacy.cycles.errors.nameRequired")); return; }
      if (formData.cycle_duration_weeks < 1) { toast.error(t("planning.legacy.cycles.errors.durationMin")); return; }
      if (formData.cycle_weeks.some((w) => !w.schedule_id)) {
        toast.error(t("planning.legacy.cycles.errors.weeksNeedSchedule"));
        return;
      }
      await planningAPI.cyclePatterns.createCyclePattern(formData);
      await loadCyclePatterns();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
    }
  };

  const handleUpdatePattern = async () => {
    try {
      if (!formData.name.trim()) { toast.error(t("planning.legacy.cycles.errors.nameRequired")); return; }
      await planningAPI.cyclePatterns.updateCyclePattern(selectedPattern.id, {
        name: formData.name,
        description: formData.description,
      });
      await loadCyclePatterns();
      setShowEditModal(false);
      setSelectedPattern(null);
      resetForm();
    } catch (error) {
    }
  };

  const handleDeletePattern = async () => {
    try {
      await planningAPI.cyclePatterns.deleteCyclePattern(selectedPattern.id);
      await loadCyclePatterns();
      setShowDeleteDialog(false);
      setSelectedPattern(null);
    } catch (error) {
    }
  };

  const handleAddWeek = () => {
    const nextWeekNumber = formData.cycle_weeks.length + 1;
    if (nextWeekNumber > formData.cycle_duration_weeks) {
      toast.error(t("planning.legacy.cycles.errors.maxWeeks", { count: formData.cycle_duration_weeks }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      cycle_weeks: [...prev.cycle_weeks, { week_number: nextWeekNumber, schedule_id: null, display_order: nextWeekNumber }],
    }));
  };

  const handleUpdateWeek = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      cycle_weeks: prev.cycle_weeks.map((week, i) => (i === index ? { ...week, [field]: value } : week)),
    }));
  };

  const handleRemoveWeek = (index) => {
    setFormData((prev) => ({ ...prev, cycle_weeks: prev.cycle_weeks.filter((_, i) => i !== index) }));
  };

  const handleAddWeekToPattern = async (patternId) => {
    try {
      const pattern = cyclePatterns.find((p) => p.id === patternId);
      const nextWeekNumber = (pattern.cycle_weeks?.length || 0) + 1;
      if (nextWeekNumber > pattern.cycle_duration_weeks) {
        toast.error(t("planning.legacy.cycles.errors.maxWeeks", { count: pattern.cycle_duration_weeks }));
        return;
      }
      const firstScheduleId = schedules[0]?.id;
      if (!firstScheduleId) { toast.error(t("planning.legacy.cycles.errors.noSchedule")); return; }
      await planningAPI.cyclePatterns.addCycleWeek(patternId, {
        week_number: nextWeekNumber,
        schedule_id: firstScheduleId,
        display_order: nextWeekNumber,
      });
      await loadCyclePatterns();
    } catch (error) {
    }
  };

  const handleDeleteWeekFromPattern = async (weekId) => {
    try {
      await planningAPI.cyclePatterns.deleteCycleWeek(weekId);
      await loadCyclePatterns();
    } catch (error) {
    }
  };

  const handleUpdateWeekInPattern = async (weekId, scheduleId) => {
    try {
      await planningAPI.cyclePatterns.updateCycleWeek(weekId, { schedule_id: scheduleId });
      await loadCyclePatterns();
      toast.success(t("planning.legacy.cycles.weekUpdated"));
    } catch (error) {
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Clock className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-muted-foreground">{t("planning.legacy.cycles.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t("planning.legacy.cycles.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("planning.legacy.cycles.subtitle")}</p>
        </div>
        <Button onClick={handleOpenCreateModal}>
          <Plus className="h-4 w-4 mr-2" /> {t("planning.legacy.cycles.new")}
        </Button>
      </div>

      <div className="grid gap-4">
        {cyclePatterns.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <Repeat className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>{t("planning.legacy.cycles.emptyTitle")}</p>
              <p className="text-sm">{t("planning.legacy.cycles.emptyDesc")}</p>
            </CardContent>
          </Card>
        ) : (
          cyclePatterns.map((pattern) => (
            <CyclePatternCard
              key={pattern.id}
              pattern={pattern}
              expandedPattern={expandedPattern}
              setExpandedPattern={setExpandedPattern}
              handleOpenEditModal={handleOpenEditModal}
              handleOpenDeleteDialog={handleOpenDeleteDialog}
              handleAddWeekToPattern={handleAddWeekToPattern}
              handleDeleteWeekFromPattern={handleDeleteWeekFromPattern}
              handleUpdateWeekInPattern={handleUpdateWeekInPattern}
              schedules={schedules}
            />
          ))
        )}
      </div>

      <CycleCreateDialog
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        formData={formData}
        setFormData={setFormData}
        schedules={schedules}
        handleCreatePattern={handleCreatePattern}
        handleAddWeek={handleAddWeek}
        handleUpdateWeek={handleUpdateWeek}
        handleRemoveWeek={handleRemoveWeek}
      />

      <CycleEditDialog
        open={showEditModal}
        onOpenChange={setShowEditModal}
        formData={formData}
        setFormData={setFormData}
        handleUpdatePattern={handleUpdatePattern}
      />

      <CycleDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        selectedPattern={selectedPattern}
        handleDeletePattern={handleDeletePattern}
      />
    </div>
  );
};

export default CyclePatternsManager;
